// UI Configuration
// This file loads and exports UI configuration from uiConfig.json

import uiConfigJson from './uiConfig.json';

// Currency Configuration
export interface CurrencyConfig {
  symbol: string;
  names: {
    major: string;
    minor: string;
  };
  locale: string;
  useIndianNumbering: boolean; // For INR, uses Lakhs/Crores instead of Thousands/Millions
}

export const CURRENCY_CONFIG: Record<string, CurrencyConfig> = {
  'INR': {
    symbol: '₹',
    names: { major: 'Rupees', minor: 'Paise' },
    locale: 'en-IN',
    useIndianNumbering: true,
  },
  'USD': {
    symbol: '$',
    names: { major: 'Dollars', minor: 'Cents' },
    locale: 'en-US',
    useIndianNumbering: false,
  },
  'EUR': {
    symbol: '€',
    names: { major: 'Euros', minor: 'Cents' },
    locale: 'de-DE',
    useIndianNumbering: false,
  },
  'GBP': {
    symbol: '£',
    names: { major: 'Pounds', minor: 'Pence' },
    locale: 'en-GB',
    useIndianNumbering: false,
  },
  'JPY': {
    symbol: '¥',
    names: { major: 'Yen', minor: 'Sen' },
    locale: 'ja-JP',
    useIndianNumbering: false,
  },
  'CNY': {
    symbol: '¥',
    names: { major: 'Yuan', minor: 'Fen' },
    locale: 'zh-CN',
    useIndianNumbering: false,
  },
  'AUD': {
    symbol: 'A$',
    names: { major: 'Australian Dollars', minor: 'Cents' },
    locale: 'en-AU',
    useIndianNumbering: false,
  },
  'CAD': {
    symbol: 'C$',
    names: { major: 'Canadian Dollars', minor: 'Cents' },
    locale: 'en-CA',
    useIndianNumbering: false,
  },
};

// Helper function to get currency config
export const getCurrencyConfig = (currencyCode: string): CurrencyConfig => {
  const code = currencyCode.toUpperCase();
  return CURRENCY_CONFIG[code] || {
    symbol: code,
    names: { major: code, minor: 'Cents' },
    locale: 'en-IN',
    useIndianNumbering: false,
  };
};

export interface UIConfig {
  reports: {
    rmInventoryReport: {
      defaultCategory: string;
    };
    materialIssueReport: {
      defaultCategory: string;
      allowedCategories: string[];
    };
    scrapReport: {
      materialIssueCategory: string;
    };
    /** HSN report: map API UOM codes (keys matched case-insensitively) to display labels */
    hsnReport: {
      uomLabels: Record<string, string>;
    };
  };
  pagination: {
    defaultItemsPerPage: number;
    itemsPerPageOptions: number[];
  };
  workOrder: {
    minRemainingQuantity: number;
  };
  dashboard: {
    rmDashboard: {
      agedInventoryMinQuantity: number;
      agedInventoryItemsPerPage: number;
    };
  };
  materialPlanning: {
    cutoffDate: number;
  };
  masterPages: {
    showColorVariant: boolean;
  };
  invoice: {
    einvoiceEnabledBranchCodes: string[];
    regularSupplies?: { code: string; name: string; description?: string }[];
    modesOfTransportation: string[];
  };
}

// Export the config with type safety
export const uiConfig: UIConfig = uiConfigJson as UIConfig;

// Helper functions to access specific config values
export const getRMInventoryReportConfig = () => uiConfig.reports.rmInventoryReport;
export const getMaterialIssueReportConfig = () => uiConfig.reports.materialIssueReport;
export const getScrapReportConfig = () => uiConfig.reports.scrapReport;
export const getHsnReportConfig = () => uiConfig.reports.hsnReport;
export const getPaginationConfig = () => uiConfig.pagination;
export const getWorkOrderConfig = () => uiConfig.workOrder;
export const getRMDashboardConfig = () => ({
  agedInventoryMinQuantity: uiConfig.dashboard.rmDashboard.agedInventoryMinQuantity,
  agedInventoryItemsPerPage: uiConfig.dashboard.rmDashboard.agedInventoryItemsPerPage,
});
export const getMaterialPlanningConfig = () => uiConfig.materialPlanning;
export const getMasterPagesConfig = () => uiConfig.masterPages;
export const getInvoiceConfig = () => uiConfig.invoice;

/** e-invoice supply types with no GST (without payment of tax). */
export const REGULAR_SUPPLY_CODES_WITHOUT_TAX = new Set(['SEZWOP', 'EXPWOP']);

export function isSupplyTypeWithoutTax(code: string | undefined | null): boolean {
  return REGULAR_SUPPLY_CODES_WITHOUT_TAX.has(String(code ?? '').trim().toUpperCase());
}

